// Pestaña Mensajes del cajón del cliente: el historial de venta o de crédito, los filtros, el
// envío a mano y, en crédito, el contador de cobranza del día.
import { useMemo, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { Button, Skeleton, usePermissions } from '@/kit';
import type { CustomerMessageHistoryItem } from '@/api/services/messages-business/messages-business.client';
import { cn } from '@/lib/cn';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import { UnavailableState } from '@/shared/patterns/DataTable';
import { DetailCard } from '@/shared/patterns/DetailCard';
import { StatusPill } from '@/shared/patterns/Pills';
import { CollectionQuotaPanel } from './CollectionQuotaPanel';
import {
    channelLabelOf,
    COPY,
    CUSTOMER_MESSAGES_PERMISSIONS,
    filterHistory,
    FILTERS_BY_CATEGORY,
    HISTORY_FAILURE_COPY,
    HISTORY_PERMISSION,
    historyFailureOf,
    MessageMomentCategoryEnum,
    momentNameOf,
    originLabelOf,
    STATUS_LABEL,
    STATUS_TONE,
    type HistoryFilter,
} from './customerMessages';
import { ManualMessagePanel } from './ManualMessagePanel';
import { useCustomerMessageHistory } from './useCustomerMessagesData';

export interface CustomerMessagesTabProps {
    retailCustomerId: string;
    category: MessageMomentCategoryEnum;
}

const TAG_CLASS =
    'inline-flex items-center rounded-sm px-1 py-[1px] text-[9px] font-bold uppercase tracking-[0.04em]';

/** `true` si el rol puede ver el historial de esa categoría: decide si el cajón monta la pestaña. */
export function useCanSeeCustomerMessages(category: MessageMomentCategoryEnum): boolean {
    const { hasPermission } = usePermissions();
    return hasPermission(HISTORY_PERMISSION[category]);
}

function MessageRow({
    item,
    showCollection,
}: {
    item: CustomerMessageHistoryItem;
    showCollection: boolean;
}) {
    return (
        <li className="border-b border-border/60 px-3 py-2 last:border-b-0">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="text-[12.5px] font-semibold leading-tight">
                        {momentNameOf(item.templateId)}
                    </p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-1 text-[10.5px] text-muted-foreground">
                        {/* El id del momento (PM-0xx) es el código que el operador busca en Mensajes a cliente. */}
                        {/* eslint-disable-next-line no-restricted-syntax */}
                        <span className="tabular-nums">{item.templateId}</span>
                        <span className={cn(TAG_CLASS, 'bg-muted text-muted-foreground')}>
                            {channelLabelOf(item.channelType)}
                        </span>
                        {showCollection && item.collection ? (
                            <span className={cn(TAG_CLASS, 'bg-warning-soft text-warning')}>
                                {COPY.collectionBadge}
                            </span>
                        ) : null}
                    </p>
                </div>
                <StatusPill tone={STATUS_TONE[item.status]}>{STATUS_LABEL[item.status]}</StatusPill>
            </div>
            <p
                className={cn(
                    'mt-1 whitespace-pre-wrap text-[11.5px] leading-snug [overflow-wrap:anywhere]',
                    item.renderedMessage === null && 'italic text-muted-foreground',
                )}
            >
                {item.renderedMessage ?? COPY.hiddenContent}
            </p>
            <p className="mt-1 text-[10px] tabular-nums text-muted-foreground">
                {formatDateTimeMx(item.createdAt)} · {originLabelOf(item.origin)}
            </p>
        </li>
    );
}

function FilterChips({
    category,
    active,
    onChange,
}: {
    category: MessageMomentCategoryEnum;
    active: HistoryFilter;
    onChange: (next: HistoryFilter) => void;
}) {
    return (
        <div role="group" aria-label={COPY.filtersLabel} className="mb-2 flex flex-wrap gap-1">
            {FILTERS_BY_CATEGORY[category].map((filter) => (
                <button
                    key={filter.key}
                    type="button"
                    aria-pressed={filter.key === active}
                    onClick={() => {
                        onChange(filter.key);
                    }}
                    className={cn(
                        'rounded-full border px-2.5 py-0.5 text-[11px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        filter.key === active
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border bg-card text-muted-foreground hover:text-foreground',
                    )}
                >
                    {filter.label}
                </button>
            ))}
        </div>
    );
}

function HistoryBody({ retailCustomerId, category }: CustomerMessagesTabProps) {
    const history = useCustomerMessageHistory(retailCustomerId, category, true);
    const [filter, setFilter] = useState<HistoryFilter>('all');
    const rows = useMemo(() => filterHistory(history.items, filter), [history.items, filter]);
    const failure = historyFailureOf(history.error);

    if (failure !== null && history.items.length === 0) {
        const copy = HISTORY_FAILURE_COPY[failure];
        return (
            <UnavailableState
                title={copy.title}
                body={copy.body}
                action={
                    <Button variant="outline" size="sm" onClick={history.refetch}>
                        {COPY.retry}
                    </Button>
                }
            />
        );
    }
    if (history.isLoading) return <Skeleton className="h-24" />;
    if (!history.hasFiadoAccount) {
        return (
            <UnavailableState
                icon={
                    <MessageSquare className="mb-3 size-9 text-muted-foreground/40" aria-hidden />
                }
                title={COPY.noAccountTitle}
                body={COPY.noAccount}
            />
        );
    }
    // Con cursor pendiente no se sabe si nunca hubo mensajes: solo que no los hay recientes.
    const emptyCopy =
        history.items.length > 0
            ? COPY.emptyFiltered
            : history.hasMore
              ? COPY.emptyRecent
              : COPY.empty;

    return (
        <>
            <FilterChips category={category} active={filter} onChange={setFilter} />
            {rows.length === 0 ? (
                <p className="px-1 py-4 text-center text-[11.5px] text-muted-foreground">
                    {emptyCopy}
                </p>
            ) : (
                <ul
                    aria-label={COPY.listLabel}
                    className="overflow-hidden rounded-md border border-border bg-card"
                >
                    {rows.map((item) => (
                        <MessageRow
                            key={item.messageId}
                            item={item}
                            showCollection={category === MessageMomentCategoryEnum.CREDIT}
                        />
                    ))}
                </ul>
            )}
            {history.hasMore ? (
                <div className="mt-2 flex flex-col items-center gap-1">
                    {failure !== null ? (
                        <p role="alert" className="text-[11px] text-destructive">
                            {COPY.loadMoreError}
                        </p>
                    ) : null}
                    <Button
                        variant="outline"
                        size="sm"
                        isLoading={history.isLoadingMore}
                        disabled={history.isLoadingMore}
                        onClick={history.loadMore}
                    >
                        {COPY.loadMore}
                    </Button>
                </div>
            ) : null}
        </>
    );
}

export function CustomerMessagesTab({ retailCustomerId, category }: CustomerMessagesTabProps) {
    const { hasPermission } = usePermissions();
    const showQuota =
        category === MessageMomentCategoryEnum.CREDIT &&
        hasPermission(CUSTOMER_MESSAGES_PERMISSIONS.collectionQuotaRead);
    if (!hasPermission(HISTORY_PERMISSION[category])) return null;

    return (
        <div className="@container space-y-2.5">
            {showQuota ? <CollectionQuotaPanel retailCustomerId={retailCustomerId} /> : null}
            <DetailCard title={COPY.historyTitle}>
                <ManualMessagePanel retailCustomerId={retailCustomerId} category={category} />
                <HistoryBody retailCustomerId={retailCustomerId} category={category} />
            </DetailCard>
        </div>
    );
}
